import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';

/* ── helpers ─────────────────────────────────────────────── */
const fmt = (d) => d ? moment(d).format('MMM D, YYYY') : null;
const arr = (a) => (Array.isArray(a) && a.length > 0) ? a : null;

const exportAgendaCSV = (agendaItems) => {
	const headers = ['Date', 'Session Name', 'Start Time', 'End Time', 'Room Type', 'Attendees', 'Setup Preference', '24hr Hold'];
	const rows = agendaItems.map(item => {
		const dates = (item.selectedDates || []).join(' | ') || 'Unassigned';
		const setup = (item.setupPreference || []).join(' | ');
		return [
			`"${dates}"`,
			`"${item.sessionName || ''}"`,
			`"${item.startTime || ''}"`,
			`"${item.endTime || ''}"`,
			`"${item.roomType || ''}"`,
			item.attendees || 0,
			`"${setup}"`,
			item.require24Hour ? 'Yes' : 'No'
		].join(',');
	});
	const csv = [headers.join(','), ...rows].join('\n');
	const blob = new Blob([csv], { type: 'text/csv' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = 'meeting-spaces.csv';
	a.click();
	URL.revokeObjectURL(url);
};

const exportRoomBlockCSV = (dailyGrid) => {
	const headers = ['Date', 'King', 'Double', 'Suite', 'Staff', 'Total'];
	const rows = dailyGrid.map(row => {
		const total = (parseInt(row.king) || 0) + (parseInt(row.double) || 0)
			+ (parseInt(row.suite) || 0) + (parseInt(row.staff) || 0);
		return [
			row.date ? `"${moment(row.date).format('ddd, MMM D')}"` : '',
			row.king || 0,
			row.double || 0,
			row.suite || 0,
			row.staff || 0,
			total
		].join(',');
	});
	const csv = [headers.join(','), ...rows].join('\n');
	const blob = new Blob([csv], { type: 'text/csv' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = 'room-block.csv';
	a.click();
	URL.revokeObjectURL(url);
};

const ReviewCard = ({ title, to, extraAction, children }) => (
	<div className="pr-review-card">
		<div className="pr-review-card-header">
			<h3 className="pr-review-card-title">{title}</h3>
			<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
				{extraAction}
				{to && (
					<Link to={to} className="pr-review-edit-btn">
						<i className="las la-pencil-alt" />
						Edit
					</Link>
				)}
			</div>
		</div>
		<div className="pr-review-card-body">{children}</div>
	</div>
);

const Field = ({ label, value }) => (value && value !== '—') ? (
	<div className="pr-review-field">
		<span className="pr-review-label">{label}</span>
		<span className="pr-review-value">{value}</span>
	</div>
) : null;

const Chips = ({ items }) => (
	<div className="pr-review-chips">
		{(items || []).map((item, i) => (
			<span key={i} className="pr-review-chip">
				{typeof item === 'string' ? item : (item.label || item.name || JSON.stringify(item))}
			</span>
		))}
	</div>
);

class ReviewSubmit extends Component {
	render() {
		const { request, orgId, requestId } = this.props;
		const base = `/${orgId}/proposal-requests/${requestId}/request`;

		const ed  = request?.eventDetails || {};
		const rb  = request?.roomBlock || {};
		const kc  = request?.keyConsiderations || {};
		const ai  = request?.additionalInfo || {};
		const pi  = request?.plannerInfo || {};
		const ag  = request?.agencyInfo || {};
		const agendaItems        = request?.agendaItems || [];
		const customConcessions  = request?.concessions?.custom || [];
		const additionalServices = request?.additionalServices || [];

		const locationsList   = [...(ed.locations?.cities || []), ...(ed.locations?.regions || []), ...(ed.locations?.countries || [])].filter(Boolean);
		const preferredHotels = (ed.locations?.hotels || []).filter(Boolean);
		const plannerName     = [pi.firstName, pi.lastName].filter(Boolean).join(' ') || pi.contactName || null;

		/* Group agenda items by date */
		const groupedAgenda = {};
		agendaItems.forEach((item) => {
			const dates = item.selectedDates || [];
			if (dates.length === 0) {
				if (!groupedAgenda['Unassigned']) groupedAgenda['Unassigned'] = [];
				groupedAgenda['Unassigned'].push(item);
			} else {
				dates.forEach((d) => {
					if (!groupedAgenda[d]) groupedAgenda[d] = [];
					groupedAgenda[d].push(item);
				});
			}
		});

		return (
			<div className="pr-review-grid">

				{/* ── Event Details ── */}
				<ReviewCard title="Event Details" to={`${base}/event`}>
					<Field label="Event Name"     value={ed.eventName} />
					<Field label="Event Type"     value={ed.eventType === 'Other' ? ed.eventTypeOther : ed.eventType} />
					<Field label="Arrival"        value={fmt(ed.arrivalDate)} />
					<Field label="Departure"      value={fmt(ed.departureDate)} />
					{arr(ed.alternateDates) && (
						<div className="pr-review-field">
							<span className="pr-review-label">Alt. Dates</span>
							<div className="pr-review-value" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
								{ed.alternateDates.map((d, i) => (
									<span key={i}>{fmt(d.arrivalDate)} – {fmt(d.departureDate)}</span>
								))}
							</div>
						</div>
					)}
					<Field label="Peak Attendees" value={ed.expectedPeakAttendees > 0 ? String(ed.expectedPeakAttendees) : null} />
					<Field label="Max Room Rate"  value={ed.maxRoomRate ? `$${Number(ed.maxRoomRate).toLocaleString()} / night` : null} />
					<Field label="Star Rating"    value={ed.hotelStarRating ? `${ed.hotelStarRating} ★` : null} />
					<Field label="Desired Brands" value={ed.desiredBrands} />
					{locationsList.length > 0 && (
						<div className="pr-review-field">
							<span className="pr-review-label">Locations</span>
							<Chips items={locationsList} />
						</div>
					)}
					{preferredHotels.length > 0 && (
						<div className="pr-review-field">
							<span className="pr-review-label">Preferred Hotels</span>
							<Chips items={preferredHotels} />
						</div>
					)}
					{arr(ed.programDays) && (
						<div className="pr-review-field">
							<span className="pr-review-label">Program Days</span>
							<Chips items={ed.programDays} />
						</div>
					)}
					{ed.openToAltArrival && arr(ed.altArrivalDays) && (
						<div className="pr-review-field">
							<span className="pr-review-label">Alt. Arrival Days</span>
							<Chips items={ed.altArrivalDays} />
						</div>
					)}
					{ed.datePatternNotes && <Field label="Pattern Notes" value={ed.datePatternNotes} />}
					{ed.locations?.avoidSourcingNotes && <Field label="Avoid Sourcing" value={ed.locations.avoidSourcingNotes} />}
					{ed.locations?.locationNotes && <Field label="Additional Location" value={ed.locations.locationNotes} />}
				</ReviewCard>

				{/* ── Room Block ── */}
				{arr(rb.dailyGrid) ? (
					<ReviewCard
						title="Room Block"
						to={`${base}/rooms`}
						extraAction={
							<button
								type="button"
								className="pr-review-edit-btn"
								onClick={() => exportRoomBlockCSV(rb.dailyGrid)}
							>
								<i className="las la-download" />
								Export CSV
							</button>
						}
					>
						<div className="pr-review-rb-table">
							<div className="pr-review-rb-head">
								<span>Date</span>
								<span>King</span>
								<span>Dbl</span>
								<span>Suite</span>
								<span>Total</span>
							</div>
							{rb.dailyGrid.map((row, i) => {
								const total = (parseInt(row.king) || 0) + (parseInt(row.double) || 0)
									+ (parseInt(row.suite) || 0) + (parseInt(row.staff) || 0);
								return (
									<div key={i} className="pr-review-rb-row">
										<span>{row.date ? moment(row.date).format('ddd, MMM D') : `Night ${i + 1}`}</span>
										<span>{row.king || 0}</span>
										<span>{row.double || 0}</span>
										<span>{row.suite || 0}</span>
										<span><strong>{total}</strong></span>
									</div>
								);
							})}
						</div>
						{rb.datesFlexible && (
							<p style={{ fontSize: 12, color: '#888', margin: '8px 0 0' }}>
								<i className="las la-check-circle" /> Dates are flexible
							</p>
						)}
					</ReviewCard>
				) : (
					<ReviewCard title="Room Block" to={`${base}/rooms`}>
						<span className="pr-review-empty">No room block defined.</span>
					</ReviewCard>
				)}

				{/* ── Meeting Space & Agenda ── */}
				<ReviewCard
					title="Meeting Space & Agenda"
					to={`${base}/agenda`}
					extraAction={agendaItems.length > 0 ? (
						<button
							type="button"
							className="pr-review-edit-btn"
							onClick={() => exportAgendaCSV(agendaItems)}
						>
							<i className="las la-download" />
							Export CSV
						</button>
					) : null}
				>
					{agendaItems.length === 0 ? (
						<span className="pr-review-empty">No agenda items added.</span>
					) : (
						Object.entries(groupedAgenda).map(([date, items]) => (
							<div key={date} style={{ marginBottom: 12 }}>
								<div className="pr-review-date-group-label">{date}</div>
								{items.map((item, i) => (
									<div key={i} className="pr-review-agenda-row">
										<span className="pr-review-agenda-time">
											{item.startTime} – {item.endTime}
										</span>
										<span className="pr-review-agenda-type">{item.roomType}</span>
										{item.attendees > 0 && (
											<span className="pr-review-meta">
												<i className="las la-user" /> {item.attendees}
											</span>
										)}
										{arr(item.setupPreference) && (
											<span className="pr-review-meta">
												<i className="las la-th" /> {item.setupPreference[0]}
											</span>
										)}
										{item.sessionName && (
											<span className="pr-review-meta pr-review-italic">{item.sessionName}</span>
										)}
										{item.require24Hour && (
											<span className="pr-review-badge">24hr</span>
										)}
									</div>
								))}
							</div>
						))
					)}
				</ReviewCard>

				{/* ── Concessions ── */}
				<ReviewCard title="Concessions" to={`${base}/concessions`}>
					{arr(customConcessions) ? (
						<Chips items={customConcessions} />
					) : (
						<span className="pr-review-empty">No concessions specified.</span>
					)}
				</ReviewCard>

				{/* ── Key Considerations ── */}
				<ReviewCard title="Key Considerations" to={`${base}/considerations`}>
					{kc.decisionFactors   && <Field label="Decision Factors"   value={kc.decisionFactors} />}
					{kc.eventHistory      && <Field label="Event History"       value={kc.eventHistory} />}
					{kc.reservationNotes  && <Field label="Reservation Notes"   value={kc.reservationNotes} />}
					{!kc.decisionFactors && !kc.eventHistory && !kc.reservationNotes && (
						<span className="pr-review-empty">No considerations noted.</span>
					)}
				</ReviewCard>

				{/* ── Additional Services ── */}
				<ReviewCard title="Additional Services" to={`${base}/services`}>
					{arr(additionalServices) ? (
						<Chips items={additionalServices} />
					) : (
						<span className="pr-review-empty">No additional services selected.</span>
					)}
				</ReviewCard>

				{/* ── Additional Info ── */}
				<ReviewCard title="Additional Info" to={`${base}/additional`}>
					{ai.responseDueDate  && <Field label="Response Due"  value={fmt(ai.responseDueDate)} />}
					{ai.decisionDate     && <Field label="Decision Date" value={fmt(ai.decisionDate)} />}
					{ai.eventDescription && <Field label="Description"   value={ai.eventDescription} />}
					{ai.budgetDirection  && <Field label="Budget"        value={ai.budgetDirection} />}
					{arr(ai.requestedAmenities) && (
						<div className="pr-review-field">
							<span className="pr-review-label">Amenities</span>
							<Chips items={ai.requestedAmenities} />
						</div>
					)}
					{!ai.responseDueDate && !ai.decisionDate && !ai.eventDescription && !ai.budgetDirection && (
						<span className="pr-review-empty">No additional info provided.</span>
					)}
				</ReviewCard>

				{/* ── Contract Signee ── */}
				<ReviewCard title="Contract Signee" to={`${base}/planner`}>
					<Field label="Name"            value={plannerName} />
					<Field label="Title"           value={pi.title} />
					<Field label="Company"         value={pi.companyName} />
					<Field label="Email"           value={pi.email} />
					<Field label="Phone"           value={pi.phone} />
					<Field label="Signer Location" value={pi.signerLocation} />
					<Field label="Due Date"        value={fmt(pi.responseDueDate)} />
					<Field label="Decision Date"   value={fmt(pi.decisionDate)} />
					{!plannerName && !pi.email && (
						<span className="pr-review-empty">No signee info provided.</span>
					)}
				</ReviewCard>

				{/* ── Agency (only if present) ── */}
				{(ag.agencyName || ag.iataNumber) && (
					<ReviewCard title="Agency" to={`${base}/agency`}>
						<Field label="Agency"         value={ag.agencyName} />
						<Field label="IATA"           value={ag.iataNumber} />
						<Field label="Commissionable" value={ag.isCommissionable ? 'Yes' : null} />
					</ReviewCard>
				)}

			</div>
		);
	}
}

export default ReviewSubmit;
